import { Injectable } from '@angular/core';
import axios, { AxiosError } from 'axios';
import { BehaviorSubject } from 'rxjs';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { global } from '../global';

@Injectable({
  providedIn: 'root',
})
export class ConnectionStatusService {
  private readonly HEALTH_POLL_INTERVAL = 5000;
  private readonly HEALTH_TIMEOUT = 4000;
  private readonly HEALTH_RETRY_DELAY = 2000;

  private isBackendDownSubject = new BehaviorSubject<boolean>(false);
  public isBackendDown$ = this.isBackendDownSubject.asObservable();

  private healthPollTimer: any = null;
  private isVerifying = false;
  // Última respuesta correcta del backend: evita marcar caída si respondió mientras se verificaba
  private lastSuccessAt = 0;
  private restoredToast: ActiveToast<any> | null = null;

  constructor(private toastr: ToastrService) {
    axios.interceptors.response.use(
      (response) => {
        if (this.targetsBackend(response.config?.url)) this.reportSuccess();
        return response;
      },
      (error: AxiosError) => {
        if (error.code === AxiosError.ERR_CANCELED) return Promise.reject(error); // navegación/abort, no caída
        if (this.targetsBackend(error.config?.url) && this.isConnectivityError(error)) {
          this.reportFailure();
        }
        return Promise.reject(error);
      }
    );
  }

  private targetsBackend(url?: string): boolean {
    return !!url && url.startsWith(global.url);
  }

  private isConnectivityError(error: AxiosError): boolean {
    if (!error.response) return true; // sin respuesta: red caída, timeout, CORS por servidor caído
    return [502, 503, 504].includes(error.response.status);
  }

  private checkHealth(): Promise<unknown> {
    return axios.get(`${global.url}health`, { timeout: this.HEALTH_TIMEOUT });
  }

  /** Confirma la caída con dos comprobaciones de /health antes de mostrar el aviso. */
  public reportFailure(): void {
    if (this.isBackendDownSubject.value || this.isVerifying) return;
    this.isVerifying = true;
    const startedAt = Date.now();
    const respondedSince = () => this.lastSuccessAt > startedAt;

    this.checkHealth()
      .catch(() => {
        if (respondedSince()) return;
        return new Promise((resolve) => setTimeout(resolve, this.HEALTH_RETRY_DELAY))
          .then(() => (respondedSince() ? undefined : this.checkHealth()))
          .catch(() => {
            if (respondedSince()) return;
            this.markDown();
          });
      })
      .finally(() => {
        this.isVerifying = false;
      });
  }

  public reportSuccess(): void {
    this.lastSuccessAt = Date.now();
    if (!this.isBackendDownSubject.value) return;
    this.isBackendDownSubject.next(false);
    this.stopHealthPolling();
    this.restoredToast = this.toastr.success('Conexión restablecida con el servidor', 'Servidor') ?? null;
  }

  private markDown(): void {
    // Que no convivan el toast de "restablecida" y la franja de reconexión
    if (this.restoredToast) {
      this.toastr.remove(this.restoredToast.toastId);
      this.restoredToast = null;
    }
    this.isBackendDownSubject.next(true);
    this.startHealthPolling();
  }

  private startHealthPolling(): void {
    if (this.healthPollTimer) return;
    this.healthPollTimer = setInterval(() => {
      // el interceptor de axios ya llama a reportSuccess()/reportFailure() según el resultado
      this.checkHealth().catch(() => {});
    }, this.HEALTH_POLL_INTERVAL);
  }

  private stopHealthPolling(): void {
    if (this.healthPollTimer) {
      clearInterval(this.healthPollTimer);
      this.healthPollTimer = null;
    }
  }
}
